import { IStringTool } from "../core/IStringTool"

type RuleType = "required" | "email" | "minLength" | "maxLength"

export type ValidationErrorCode =
  | "required"
  | "invalid-email"
  | "too-short"
  | "too-long"

export type ValidationRule =
  | "required"
  | "email"
  | { type: "minLength"; value: number }
  | { type: "maxLength"; value: number }

interface ValidationInput {
  /** Value to validate. */
  value: string

  /** List of rules to apply. */
  rules: ValidationRule[]

  /**
   * Optional message map: key = errorCode, value = string or function returning a message.
   */
  messages?: Partial<
    Record<
      ValidationErrorCode,
      string | ((args: { value: string; rule: ValidationRule }) => string)
    >
  >
}

export interface ValidationOutput {
  /** `true` if valid, `false` if error found. */
  valid: boolean

  /** One of the known error codes (or null if no error). */
  error: ValidationErrorCode | null

  /** Final message (either default or user-defined). */
  message: string | null
}

/**
 * Tool for validating strings using rules like `required`, `minLength`, etc.
 * Allows custom error messages (as string or generator function).
 */
export default class ValidationTool implements IStringTool<ValidationInput, ValidationOutput> {
  /**
   * Validates input value and returns error code + message.
   */
  process({ value, rules, messages = {} }: ValidationInput): ValidationOutput {
    for (const rule of rules) {
      let code: ValidationErrorCode | null = null

      if (rule === "required" && value.trim() === "") {
        code = "required"
      } else if (rule === "email" && !/^\S+@\S+\.\S+$/.test(value)) {
        code = "invalid-email"
      } else if (typeof rule === "object") {
        if (rule.type === "minLength" && value.length < rule.value) code = "too-short"
        if (rule.type === "maxLength" && value.length > rule.value) code = "too-long"
      }

      if (code) {
        const raw = messages[code]
        const message =
          typeof raw === "function" ? raw({ value, rule }) : raw ?? this.defaultMessage(code, rule)
        return { valid: false, error: code, message }
      }
    }

    return { valid: true, error: null, message: null }
  }

  private defaultMessage(code: ValidationErrorCode, rule?: ValidationRule): string {
    switch (code) {
      case "required":
        return "This field is required."
      case "invalid-email":
        return "Please enter a valid email."
      case "too-short":
        return `Too short${typeof rule === "object" && "value" in rule ? ` (min ${rule.value})` : ""}.`
      case "too-long":
        return `Too long${typeof rule === "object" && "value" in rule ? ` (max ${rule.value})` : ""}.`
    }
  }
}
